import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Dog, Cat, ArrowRight, ArrowLeft, RotateCcw, Stethoscope, Siren, Wallet, MessageCircle, Tag, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtEur } from "@/lib/api";
import { DiscountForm } from "./DiscountForm";
import { PolicyCard } from "./PolicyCard";
import { CallbackModal } from "./CallbackModal";

const RISK = {
  basso: { label: "Basso", color: "text-emerald-500", bar: "bg-emerald-500" },
  medio: { label: "Medio", color: "text-amber-500", bar: "bg-amber-500" },
  alto: { label: "Alto", color: "text-rose-500", bar: "bg-rose-500" },
};

function Stat({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1.5"><Icon size={14} /> {label}</div>
      <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function Calculator({ preset }) {
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(preset?.breed ? 3 : 1);
  const [form, setForm] = useState({ pet_type: preset?.pet_type || "", breed: preset?.breed || "", age: 3, city: preset?.city || "" });
  const [result, setResult] = useState(null);
  const [callback, setCallback] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/calculator/options").then((r) => setOpts(r.data)).catch(() => {});
    const shared = new URLSearchParams(window.location.search).get("r");
    if (shared) api.get(`/public/calculator/result/${shared}`).then((r) => { setResult(r.data); setStep(5); }).catch(() => {});
  }, []);

  const shareUrl = result?.id ? `${window.location.origin}/preventivo?r=${result.id}` : "";
  const shareText = result ? `Ho calcolato quanto rischio di spendere dal veterinario per il mio ${result.breed} a ${result.city}: ${result.annual_vet_cost_min}-${result.annual_vet_cost_max}€ l'anno. Prova anche tu, è gratis: ${shareUrl}` : "";

  const breeds = form.pet_type === "gatto" ? opts?.cat_breeds : opts?.dog_breeds;
  const next = () => setStep((s) => s + 1);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/calculator/estimate", { ...form, age: Number(form.age) });
      setResult(data);
      setStep(5);
    } catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setForm({ pet_type: "", breed: "", age: 3, city: "" }); setStep(1); window.history.replaceState(null, "", window.location.pathname); };
  useEffect(() => {
    document.body.style.overflow = step === 5 && result ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [step, result]);

  return (
    <div id="calcolatore" className="relative surface p-6 sm:p-8 shadow-2xl shadow-emerald-500/10 border-emerald-500/30 scroll-mt-24" data-testid="calculator">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="label-mono text-emerald-600">Calcolatore gratuito · risultato subito</p>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Quanto rischi di spendere dal veterinario?</h2>
        </div>
        {step < 5 && <span className="font-mono text-xs text-muted-foreground" data-testid="calculator-step-indicator">{step}/4</span>}
      </div>
      {step < 5 && (
        <div className="-mt-3 mb-5 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800" data-testid="calculator-discount-promise">
          <Tag size={13} /> Alla fine ti sblocchiamo un codice sconto da 25€
        </div>
      )}
      {step < 5 && (
        <div className="h-1 w-full bg-muted rounded-full mb-7 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${(step / 4) * 100}%` }} />
        </div>
      )}

      {step === 1 && (
        <div className="reveal">
          <p className="font-medium mb-4">Che animale hai?</p>
          <div className="grid grid-cols-2 gap-3">
            {[["cane", Dog, "Cane"], ["gatto", Cat, "Gatto"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => { setForm({ ...form, pet_type: v, breed: "" }); next(); }} data-testid={`calculator-step-1-pet-type-${v === "cane" ? "dog" : "cat"}`}
                className="group flex flex-col items-center gap-3 rounded-xl border border-border p-6 hover:border-emerald-500 hover:bg-emerald-500/5 transition-colors">
                <Icon size={36} className="text-muted-foreground group-hover:text-emerald-500 transition-colors" />
                <span className="font-display font-semibold">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="reveal">
          <p className="font-medium mb-4">Che razza è il tuo {form.pet_type}?</p>
          <select className="input" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="calculator-breed-select">
            <option value="">Seleziona la razza</option>
            {(breeds || []).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <div className="flex justify-between mt-6">
            <button className="btn-ghost" onClick={() => setStep(1)} data-testid="calculator-back-btn"><ArrowLeft size={16} /> Indietro</button>
            <button className="btn-primary" disabled={!form.breed} onClick={next} data-testid="calculator-next-btn">Avanti <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="reveal">
          <p className="font-medium mb-1">Quanti anni ha?</p>
          <p className="text-sm text-muted-foreground mb-5">Usa 0 per cuccioli sotto l'anno.</p>
          <div className="flex items-center gap-5">
            <input type="range" min="0" max="18" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="flex-1 accent-emerald-500" data-testid="calculator-age-input" />
            <span className="font-display text-3xl font-bold w-20 text-right" data-testid="calculator-age-value">{form.age} <span className="text-sm text-muted-foreground font-normal">anni</span></span>
          </div>
          <div className="flex justify-between mt-8">
            <button className="btn-ghost" onClick={() => setStep(2)}><ArrowLeft size={16} /> Indietro</button>
            <button className="btn-primary" onClick={next} data-testid="calculator-next-btn">Avanti <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="reveal">
          <p className="font-medium mb-4">In che città vivi?</p>
          <select className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="calculator-city-input">
            <option value="">Seleziona la città</option>
            {(opts?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex justify-between mt-6">
            <button className="btn-ghost" onClick={() => setStep(3)}><ArrowLeft size={16} /> Indietro</button>
            <button className="btn-primary" disabled={!form.city || loading} onClick={submit} data-testid="calculator-submit-btn">{loading ? "Calcolo..." : "Calcola il rischio"}</button>
          </div>
        </div>
      )}

      {step === 5 && result && createPortal(
        <div className="fixed inset-0 z-[95] overflow-y-auto bg-background" data-testid="calculator-result">
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/95 backdrop-blur px-4 py-3">
            <button onClick={() => { reset(); window.scrollTo({ top: 0 }); }} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted" data-testid="result-back-home"><ArrowLeft size={14} /> Home</button>
            <button className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted" onClick={reset} data-testid="calculator-reset-btn"><RotateCcw size={13} /> Ricalcola</button>
          </div>
          <div className="mx-auto max-w-2xl px-4 py-10 reveal">
            <p className="text-center text-sm text-muted-foreground">{result.breed} · {result.age} anni · {result.city}</p>
            <p className={`text-center font-display text-5xl sm:text-7xl font-extrabold tracking-tight mt-3 ${RISK[result.risk_level].color}`} data-testid="calculator-risk-level">Rischio {RISK[result.risk_level].label.toLowerCase()}</p>
            <div className="h-3 w-full bg-muted rounded-full overflow-hidden mt-6 mb-10">
              <div className={`h-full ${RISK[result.risk_level].bar} transition-[width] duration-700`} style={{ width: `${Math.max(result.risk_percent, 8)}%` }} />
            </div>
            <div className="grid gap-3 sm:grid-cols-3 mb-8">
              <Stat icon={Stethoscope} label="Spesa veterinaria annua stimata" value={`${fmtEur(result.annual_vet_cost_min)} – ${fmtEur(result.annual_vet_cost_max)}`} hint={`Indice città ${result.city_index}`} testId="result-annual-cost" />
              <Stat icon={Siren} label="Emergenza chirurgica tipica" value={`${fmtEur(result.emergency_cost_min)} – ${fmtEur(result.emergency_cost_max)}`} hint="Es. corpo estraneo, frattura, TPLO" testId="result-emergency-cost" />
              <Stat icon={Wallet} label="Premio polizza indicativo" value={`${result.premium_monthly_min}–${result.premium_monthly_max}€/mese`} hint="Massimale 3.000-5.000€" testId="result-premium" />
            </div>
            {result.pathologies?.length > 0 && (
              <div className="surface p-5 mb-8" data-testid="result-pathologies">
                <p className="label-mono text-rose-500 mb-3">Le 3 patologie più frequenti per {result.breed}</p>
                <ul className="divide-y divide-border">
                  {result.pathologies.map((p) => (
                    <li key={p.name} className="flex items-center justify-between gap-3 py-2.5 text-sm"><span>{p.name}</span><span className="num text-muted-foreground whitespace-nowrap">{fmtEur(p.cost_min)} – {fmtEur(p.cost_max)}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {result.policies?.length > 0 && (
              <div className="mb-8 pt-3" data-testid="result-policies">
                <p className="label-mono text-emerald-500 mb-3">La nostra scelta per {result.breed}</p>
                <PolicyCard p={result.policies[0]} compact hidePromo pet={{ pet_type: result.pet_type, breed: result.breed, age: result.age, city: result.city }} estimate={result} />
              </div>
            )}
            <DiscountForm pet={{ pet_type: result.pet_type, breed: result.breed, age: result.age, city: result.city }} estimate={result} />
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              <button className="btn-ghost !py-2 text-sm" onClick={() => setCallback(true)} data-testid="result-callback-btn"><PhoneCall size={14} /> Preferisci parlare? Ti richiamiamo</button>
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="btn-ghost !py-2 text-sm" data-testid="calculator-share-whatsapp"><MessageCircle size={14} /> Condividi su WhatsApp</a>
            </div>
            {callback && <CallbackModal onClose={() => setCallback(false)} pet={{ pet_type: result.pet_type, breed: result.breed, age: result.age, city: result.city }} estimate={result} source="calculator-callback" />}
            <p className="text-[11px] text-muted-foreground mt-8 text-center">Stima basata su dati ANMVI e listini di 60 cliniche, per razza, età e città. Non è un preventivo assicurativo.</p>
          </div>
        </div>, document.body)}
    </div>
  );
}
